import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import {
  CalendarClock,
  Globe,
  CircleCheck,
  Copy,
  Eye,
  Lock,
  LockOpen,
  Mail,
  MailCheck,
  Monitor,
  OctagonAlert,
  Package,
  RotateCw,
  Save,
  Search,
  Send,
  SendHorizontal,
  Smartphone,
  Sparkles,
  TriangleAlert,
  Type,
  Users,
  Zap,
} from 'lucide-react';
import '../styles/preflight.css';
import { Switch } from '../components/ui/Switch';
import { SkipLink } from '../components/SkipLink';
import { BlockEditor } from '../components/BlockEditor';
import {
  AudiencePicker,
  EMPTY_AUDIENCE,
  SenderSelect,
  useSenders,
} from '../components/AudiencePicker';
import { EditorNav } from '../components/campaign-editor/EditorNav';
import { ChecklistStep } from '../components/campaign-editor/ChecklistStep';
import { FlightCard } from '../components/campaign-editor/FlightCard';
import { StepGlyph } from '../components/campaign-editor/StepGlyph';
import { SegmentedControl } from '../components/campaign-editor/SegmentedControl';
import { ToastStack, useToasts } from '../components/campaign-editor/Toasts';
import { CommandPalette, type PaletteItem } from '../components/campaign-editor/CommandPalette';
import { SaveAsTemplateSheet } from '../components/campaign-editor/SaveAsTemplateSheet';
import { HtmlSourceEditor } from '../components/html-template/HtmlSourceEditor';
import { useHtmlCheck } from '../components/html-template/useHtmlCheck';
import { useRehostImages } from '../components/html-template/useRehostImages';
import { SectionEditsNotice } from '../components/section-editor/SectionEditsNotice';
import { CopyImagesPanel } from '../components/html-template/CopyImagesPanel';
import { EmailPreview, type PreviewDevice } from '../components/campaign-editor/EmailPreview';
import { PreviewAsContact } from '../components/campaign-editor/PreviewAsContact';
import {
  MOBILE_QUERY,
  prefersReducedMotion,
  useMediaQuery,
} from '../components/campaign-editor/hooks';
import { apiFetch, MOCK_MODE } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDuplicateCampaign } from '../lib/useDuplicateCampaign';
import {
  browserTimeZone,
  formatDateTime,
  formatSendTime,
  fromLocalInputs,
  pad2,
  timeToMinutes,
  toLocalInputs,
} from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { useFooterDefaults } from '../lib/useFooterDefaults';
import {
  blocksFromJson,
  blocksToHtml,
  seedBlocks,
  validateBlocks,
  type Block,
} from '../lib/blocks';
import {
  ALL_STEPS,
  MAIN_STEPS,
  STEP_LABELS,
  PREHEADER_MAX,
  SUBJECT_MAX,
  checkSchedule,
  isValidTimeZone,
  computeSteps,
  isReady,
  mainReady,
  nextTarget,
  readyCount,
  stepForCheck,
  type ChecksState,
  type StepId,
  type StepState,
} from '../lib/preflightSteps';
import {
  ApiError,
  type AudienceEstimate,
  type SmartSendCoverage,
  type Campaign,
  type CampaignAudience,
  type CheckStatus,
  type MailingList,
  type PreflightResult,
} from '../types/api';

/**
 * Message for a failed campaign load. Only a real 404 is "not
 * found" — a 403 or a network/server error must not tell the user the
 * campaign doesn't exist.
 */
export function campaignLoadErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) return 'The campaign was not found.';
  if (error instanceof ApiError && error.status === 403) {
    return 'You do not have access to this campaign.';
  }
  return 'The campaign could not be loaded. Try again shortly.';
}

interface FormValues {
  name: string;
  subject: string;
  senderId: string;
  audience: CampaignAudience;
  abEnabled: boolean;
  abSplitPercent: number;
  abWaitHours: number;
  subjectB: string;
  /** K6: content B (HTML) of the A/B test; '' = both groups get the same content. */
  htmlB: string;
  /** K6: send the winner to the rest automatically (off unless ticked). */
  abAutoSend: boolean;
  abMetric: 'clicks' | 'opens';
  /** Personal discount code per recipient (migration 0054). */
  couponEnabled: boolean;
  couponPercent: number;
  couponValidDays: number;
  couponPrefix: string;
  preheader: string;
  blocks: Block[];
  editorMode: 'loading' | 'blocks' | 'html';
  /** The campaign's HTML in `html` mode, the source of truth there. */
  html: string;
  /** The chosen send time (UTC epoch ms) or null = right after approval. */
  scheduledAt: number | null;
  /** K6: send at a local time per recipient's country (see the Send time step). */
  localSend: { enabled: boolean; date: string; time: string; fallbackTz: string };
  /** K6 smart send time: each contact at their usual hour inside a 24 h window from `scheduledAt`. */
  smartSend: boolean;
}

/** Everything a save writes, as one comparable string (dirty tracking). */
function saveSnapshot(v: FormValues): string {
  return JSON.stringify({
    ...v,
    subjectB: v.abEnabled ? v.subjectB : '',
    htmlB: v.abEnabled ? v.htmlB : '',
    // Settings typed while the code is off are not saved, so they do not count.
    couponPercent: v.couponEnabled ? v.couponPercent : 0,
    couponValidDays: v.couponEnabled ? v.couponValidDays : 0,
    couponPrefix: v.couponEnabled ? v.couponPrefix : '',
    blocks: v.editorMode === 'blocks' ? v.blocks : null,
    html: v.editorMode === 'html' ? v.html : null,
  });
}

/** What the pre-send checks look at; the internal name does not make a run stale. */
function checksSnapshot(v: FormValues): string {
  // The preview text is not part of any check either.
  return saveSnapshot({ ...v, name: '', preheader: '' });
}

// Personal discount code limits, like the API and migration 0054.
const COUPON_PERCENT_MIN = 1;
const COUPON_PERCENT_MAX = 50;
const COUPON_PERCENT_DEFAULT = 5;
const COUPON_VALID_DAYS_MIN = 1;
const COUPON_VALID_DAYS_MAX = 30;
const COUPON_VALID_DAYS_DEFAULT = 14;
const COUPON_PREFIX_MAX = 8;
const COUPON_PREFIX_DEFAULT = 'NP';

/** What is wrong with the discount code settings, or null when the API will take them. */
function couponSettingsProblem(percent: number, validDays: number, prefix: string): string | null {
  if (!Number.isInteger(percent) || percent < COUPON_PERCENT_MIN || percent > COUPON_PERCENT_MAX) {
    return `The discount must be a whole number from ${COUPON_PERCENT_MIN} to ${COUPON_PERCENT_MAX} %.`;
  }
  if (
    !Number.isInteger(validDays) ||
    validDays < COUPON_VALID_DAYS_MIN ||
    validDays > COUPON_VALID_DAYS_MAX
  ) {
    return `The code must be valid for ${COUPON_VALID_DAYS_MIN} to ${COUPON_VALID_DAYS_MAX} days.`;
  }
  if (!/^[A-Z0-9]{2,8}$/.test(prefix)) {
    return 'The prefix must be 2 to 8 letters (A-Z) or digits.';
  }
  return null;
}

const CHECK_STATE: Record<CheckStatus, StepState> = {
  ok: 'complete',
  warn: 'warning',
  fail: 'warning',
};

const STEP_ICONS: Record<StepId, ReactNode> = {
  to: <Users aria-hidden="true" />,
  from: <Mail aria-hidden="true" />,
  subject: <Type aria-hidden="true" />,
  content: <Package aria-hidden="true" />,
  time: <CalendarClock aria-hidden="true" />,
  checks: <CircleCheck aria-hidden="true" />,
  send: <Send aria-hidden="true" />,
};

const REVEAL_FIRST_MS = 450;
const REVEAL_STEP_MS = 320;

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return (
    t.isContentEditable ||
    t instanceof HTMLInputElement ||
    t instanceof HTMLTextAreaElement ||
    t instanceof HTMLSelectElement
  );
}

function EditorFrame({ children }: { children: ReactNode }) {
  return (
    <div className="pf">
      <SkipLink />
      <EditorNav />
      <main id="main-content" className="pf-editor">
        {children}
      </main>
    </div>
  );
}

export function KampagneEditPage() {
  const { id } = useParams<{ id: string }>();
  const { isEditor, user } = useAuth();
  const navigate = useNavigate();
  const { duplicate, pending: duplicating, error: duplicateError } = useDuplicateCampaign();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => apiFetch<{ campaign: Campaign }>(`/campaigns/${id}`),
    enabled: Boolean(id),
  });
  const campaign = data?.campaign;

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [senderId, setSenderId] = useState('');
  const [audience, setAudience] = useState<CampaignAudience>(EMPTY_AUDIENCE);
  // Phase 6 A/B subject-line test.
  const [abEnabled, setAbEnabled] = useState(false);
  const [abSplitPercent, setAbSplitPercent] = useState(15);
  const [abWaitHours, setAbWaitHours] = useState(4);
  const [subjectB, setSubjectB] = useState('');
  // K6: A/B test of the content. Content B is edited as HTML (a copy of A's
  // to start with), so a campaign never needs a second block model.
  const [contentBEnabled, setContentBEnabled] = useState(false);
  const [htmlB, setHtmlB] = useState('');
  const [abAutoSend, setAbAutoSend] = useState(false);
  const [abMetric, setAbMetric] = useState<'clicks' | 'opens'>('clicks');
  // Personal discount code per recipient (migration 0054).
  const [couponEnabled, setCouponEnabled] = useState(false);
  const [couponPercent, setCouponPercent] = useState(COUPON_PERCENT_DEFAULT);
  const [couponValidDays, setCouponValidDays] = useState(COUPON_VALID_DAYS_DEFAULT);
  const [couponPrefix, setCouponPrefix] = useState(COUPON_PREFIX_DEFAULT);
  const [preheader, setPreheader] = useState('');
  // Send time: the date and time are typed in the browser's zone and stored as UTC.
  const [sendMode, setSendMode] = useState<'now' | 'later' | 'local' | 'smart'>('now');
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  // Local-time sending: a wall-clock date and time that apply in every recipient's own zone.
  const [localDate, setLocalDate] = useState('');
  const [localTime, setLocalTime] = useState('');
  const [fallbackTz, setFallbackTz] = useState('');
  const timeZone = browserTimeZone();
  const scheduledAt =
    sendMode === 'later' || sendMode === 'smart' ? fromLocalInputs(schedDate, schedTime) : null;
  // Re-evaluated every 30 s so a time that slips into the past while the page is open shows up.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const [blocks, setBlocks] = useState<Block[]>(seedBlocks);
  const footerDefaults = useFooterDefaults();
  const [htmlSource, setHtmlSource] = useState('');
  // `html` = a campaign without a block model: made from an HTML template, or
  // a draft saved before campaigns stored their block JSON. Its HTML is
  // edited as source (never rebuilt from blocks); the block editor only
  // takes over when the user explicitly chooses "Replace with blocks"
  // (review fix 25/9 — the editor used to open blank here and "Save draft"
  // overwrote the real content).
  const [editorMode, setEditorMode] = useState<'loading' | 'blocks' | 'html'>('loading');
  const hydratedFor = useRef<string | null>(null);
  const htmlCheck = useHtmlCheck(htmlSource, editorMode === 'html');
  const rehost = useRehostImages(htmlSource, setHtmlSource);
  const copyingImages = rehost.run.status === 'running';
  const [baseline, setBaseline] = useState<string | null>(null);

  // Test mails may only go to users of this tenant (security fixes round
  // 2) — start from the caller's own address.
  const [testEmail, setTestEmail] = useState('');
  const ownEmail = user?.email ?? null;
  useEffect(() => {
    if (ownEmail) setTestEmail((current) => current || ownEmail);
  }, [ownEmail]);
  const [testSendMsg, setTestSendMsg] = useState<string | null>(null);

  const [openStep, setOpenStep] = useState<StepId | null>(null);
  const [openBlockId, setOpenBlockId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ id: string; n: number } | null>(null);
  const [typingKey, setTypingKey] = useState(0);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [checks, setChecks] = useState<ChecksState>({ phase: 'idle', checks: [] });
  const [revealed, setRevealed] = useState(0);
  const [checkedSnap, setCheckedSnap] = useState<string | null>(null);
  const [checksError, setChecksError] = useState<string | null>(null);
  const { toasts, push: toast, leave: leaveToast } = useToasts();

  const fabRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const focusStepRef = useRef<StepId | null>(null);
  const revealTimers = useRef<number[]>([]);
  const checksBusy = useRef(false);

  const values: FormValues = {
    name,
    subject,
    senderId,
    audience,
    abEnabled,
    abSplitPercent,
    abWaitHours,
    subjectB,
    htmlB: contentBEnabled ? htmlB : '',
    abAutoSend,
    abMetric,
    couponEnabled,
    couponPercent,
    couponValidDays,
    couponPrefix,
    preheader,
    blocks,
    editorMode,
    html: htmlSource,
    scheduledAt,
    smartSend: sendMode === 'smart',
    localSend: {
      enabled: sendMode === 'local',
      date: sendMode === 'local' ? localDate : '',
      time: sendMode === 'local' ? localTime : '',
      fallbackTz: sendMode === 'local' ? fallbackTz.trim() : '',
    },
  };
  const couponBody = couponEnabled
    ? { percent: couponPercent, validDays: couponValidDays, prefix: couponPrefix }
    : null;
  const couponProblem = couponEnabled
    ? couponSettingsProblem(couponPercent, couponValidDays, couponPrefix)
    : null;
  const snap = saveSnapshot(values);
  const checkSnap = checksSnapshot(values);
  const dirty = baseline !== null && snap !== baseline;

  useEffect(() => {
    if (!data?.campaign) return;
    const c = data.campaign;
    setName(c.name);
    setSubject(c.subject);
    setSenderId(c.senderId);
    // Blocks are hydrated once per campaign — a background refetch must
    // not throw away unsaved edits or undo "Start over".
    if (hydratedFor.current !== c.id) {
      hydratedFor.current = c.id;
      const saved = blocksFromJson(c.blockJson);
      const hydrated: FormValues = {
        name: c.name,
        subject: c.subject,
        senderId: c.senderId,
        audience: c.audience ?? EMPTY_AUDIENCE,
        abEnabled: c.abEnabled,
        abSplitPercent: c.abSplitPercent,
        abWaitHours: c.abWaitHours,
        subjectB: c.subjectB ?? '',
        htmlB: c.htmlB ?? '',
        abAutoSend: c.abAutoSend ?? false,
        abMetric: c.abMetric ?? 'clicks',
        couponEnabled: Boolean(c.coupon),
        couponPercent: c.coupon?.percent ?? COUPON_PERCENT_DEFAULT,
        couponValidDays: c.coupon?.validDays ?? COUPON_VALID_DAYS_DEFAULT,
        couponPrefix: c.coupon?.prefix ?? COUPON_PREFIX_DEFAULT,
        preheader: c.preheader ?? '',
        blocks: saved ?? seedBlocks(),
        editorMode: saved ? 'blocks' : 'html',
        html: c.html,
        scheduledAt: c.scheduledAt,
        smartSend: c.sendMode === 'smart',
        localSend: {
          enabled: c.sendMode === 'local_time',
          date: c.sendMode === 'local_time' ? (c.localSendDate ?? '') : '',
          time:
            c.sendMode === 'local_time' && c.localSendMinutes != null
              ? `${pad2(Math.floor(c.localSendMinutes / 60))}:${pad2(c.localSendMinutes % 60)}`
              : '',
          fallbackTz: c.sendMode === 'local_time' ? (c.sendFallbackTz ?? '') : '',
        },
      };
      if (c.sendMode === 'local_time') {
        setSendMode('local');
        setLocalDate(hydrated.localSend.date);
        setLocalTime(hydrated.localSend.time);
        setFallbackTz(hydrated.localSend.fallbackTz);
      } else if (c.scheduledAt !== null) {
        const local = toLocalInputs(c.scheduledAt);
        setSendMode(c.sendMode === 'smart' ? 'smart' : 'later');
        setSchedDate(local.date);
        setSchedTime(local.time);
      }
      setAudience(hydrated.audience);
      setAbEnabled(hydrated.abEnabled);
      setAbSplitPercent(hydrated.abSplitPercent);
      setAbWaitHours(hydrated.abWaitHours);
      setSubjectB(hydrated.subjectB);
      setContentBEnabled(hydrated.htmlB.trim() !== '');
      setHtmlB(hydrated.htmlB);
      setAbAutoSend(hydrated.abAutoSend);
      setAbMetric(hydrated.abMetric);
      setCouponEnabled(hydrated.couponEnabled);
      setCouponPercent(hydrated.couponPercent);
      setCouponValidDays(hydrated.couponValidDays);
      setCouponPrefix(hydrated.couponPrefix);
      setPreheader(hydrated.preheader);
      if (saved) setBlocks(saved);
      setHtmlSource(c.html);
      setEditorMode(hydrated.editorMode);
      setBaseline(saveSnapshot({ ...hydrated, blocks: saved ?? [] }));
    }
  }, [data?.campaign]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const sentSnap = snap;
      // A time that is not valid right now is not sent: the API would refuse the whole
      // save. The stored time stays and the Send time step says what to fix.
      const localMinutes = timeToMinutes(localTime);
      const scheduleBody =
        sendMode === 'local'
          ? // The API derives scheduled_at from the date and time; a missing part is not sent.
            localDate && localMinutes !== null
            ? {
                sendMode: 'local_time' as const,
                localSendDate: localDate,
                localSendMinutes: localMinutes,
                sendFallbackTz: fallbackTz.trim() || null,
              }
            : {}
          : sendMode === 'now'
            ? { sendMode: 'fixed' as const, scheduledAt: null }
            : scheduledAt !== null && checkSchedule(scheduledAt, Date.now()) === null
              ? {
                  sendMode: sendMode === 'smart' ? ('smart' as const) : ('fixed' as const),
                  scheduledAt,
                }
              : { sendMode: sendMode === 'smart' ? ('smart' as const) : ('fixed' as const) };
      const result = await apiFetch<{ campaign: Campaign }>(`/campaigns/${id}`, {
        method: 'PATCH',
        // An HTML campaign saves its source as html and never a block model.
        body:
          editorMode === 'blocks'
            ? {
                name,
                subject,
                senderId,
                audience,
                html: blocksToHtml(blocks),
                blockJson: { blocks },
                abEnabled,
                abSplitPercent,
                abWaitHours,
                subjectB: abEnabled ? subjectB || null : null,
                htmlB: abEnabled && contentBEnabled && htmlB.trim() ? htmlB : null,
                abAutoSend,
                abMetric,
                coupon: couponBody,
                preheader: preheader.trim() || null,
                ...scheduleBody,
              }
            : {
                name,
                subject,
                senderId,
                audience,
                ...(editorMode === 'html' && htmlSource.trim() ? { html: htmlSource } : {}),
                abEnabled,
                abSplitPercent,
                abWaitHours,
                subjectB: abEnabled ? subjectB || null : null,
                htmlB: abEnabled && contentBEnabled && htmlB.trim() ? htmlB : null,
                abAutoSend,
                abMetric,
                coupon: couponBody,
                preheader: preheader.trim() || null,
                ...scheduleBody,
              },
      });
      return { result, sentSnap };
    },
    onSuccess: ({ sentSnap }) => {
      setBaseline(sentSnap);
      void queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const saveError =
    saveMutation.error instanceof ApiError && saveMutation.error.body.message
      ? saveMutation.error.body.message
      : saveMutation.isError
        ? 'The draft could not be saved. Try again.'
        : null;

  const readOnly = !isEditor || (campaign !== undefined && campaign.status !== 'draft');
  const canSave =
    !readOnly && editorMode !== 'loading' && Boolean(senderId) && couponProblem === null;

  const saveDraft = useCallback(() => {
    if (!canSave || saveMutation.isPending) return;
    saveMutation.mutate(undefined, {
      onSuccess: () =>
        toast({ title: 'Draft saved', description: 'All changes are stored.', icon: Save }),
    });
  }, [canSave, saveMutation, toast]);

  // test-send and /preflight run on the stored campaign, so unsaved edits go first.
  const saveIfDirty = useCallback(async () => {
    if (dirty && canSave) await saveMutation.mutateAsync();
  }, [dirty, canSave, saveMutation]);

  const testSendMutation = useMutation({
    mutationFn: async (toEmail: string) => {
      await saveIfDirty();
      return apiFetch(`/campaigns/${id}/test-send`, {
        method: 'POST',
        body: { toEmail },
      });
    },
    onSuccess: (_data, toEmail) => {
      setTestSendMsg('Test email sent.');
      toast({
        title: 'Test email sent',
        description: `Sent to ${toEmail}. Check your inbox in a moment.`,
        icon: MailCheck,
      });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && (err.status === 403 || err.status === 400) && err.body.message
          ? err.body.message
          : 'The test email could not be sent.';
      setTestSendMsg(msg);
      toast({ title: 'Test email not sent', description: msg, icon: TriangleAlert, tone: 'muted' });
    },
  });
  const canSendTest = Boolean(isEditor) && campaign?.status === 'draft';
  // The preview bar's "Send test" works before the Send step unlocks, so a
  // missing address must be said in a toast, not only inside that step.
  const sendTest = useCallback(() => {
    const toEmail = testEmail.trim();
    if (!toEmail) {
      const msg = ownEmail
        ? 'Enter an email address for the test.'
        : 'Your own email address is missing. Reload the page and try again.';
      setTestSendMsg(msg);
      toast({ title: 'Test email not sent', description: msg, icon: TriangleAlert, tone: 'muted' });
      return;
    }
    testSendMutation.mutate(toEmail);
  }, [testEmail, ownEmail, testSendMutation, toast]);

  // --- audience summary (same queries and keys as AudiencePicker, so the cache is shared) ---
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: () => apiFetch<{ items: MailingList[] }>('/lists'),
  });
  const debouncedAudience = useDebouncedValue(audience, 300);
  const estimate = useQuery({
    queryKey: ['audience-estimate', debouncedAudience],
    queryFn: () =>
      apiFetch<AudienceEstimate>('/campaigns/audience-estimate', {
        method: 'POST',
        body: debouncedAudience,
      }),
  });
  // K6 smart send time: how many recipients have a usual hour. One D1 query,
  // only while the Smart choice is on and the audience has settled.
  const smartCoverage = useQuery({
    queryKey: ['smart-send-coverage', debouncedAudience],
    enabled: sendMode === 'smart' && debouncedAudience.listIds.length > 0,
    queryFn: () =>
      apiFetch<SmartSendCoverage>('/campaigns/smart-send-coverage', {
        method: 'POST',
        body: debouncedAudience,
      }),
  });
  const senders = useSenders();
  const senderRow = senders.data?.items.find((s) => s.id === senderId) ?? null;

  const listNames = audience.listIds.map(
    (lid) => lists.data?.items.find((l) => l.id === lid)?.name ?? 'Archived list',
  );
  const estimateFresh = debouncedAudience === audience && !estimate.isFetching;

  // --- checks: stale once the checked content changes ---
  const checksPhase =
    (checks.phase === 'pass' || checks.phase === 'fail') && checkedSnap !== checkSnap
      ? 'stale'
      : checks.phase;
  const effectiveChecks: ChecksState = { ...checks, phase: checksPhase };

  const steps = computeSteps({
    listNames,
    estimate: estimateFresh ? (estimate.data?.estimatedRecipients ?? null) : null,
    estimateError: estimate.isError,
    sender: senderRow
      ? {
          fromName: senderRow.fromName,
          fromEmail: senderRow.fromEmail,
          verified: senderRow.domainStatus === 'verified',
        }
      : null,
    subject,
    abEnabled,
    subjectB,
    htmlB: contentBEnabled ? htmlB : '',
    editorMode,
    blocks,
    html: htmlSource,
    checks: effectiveChecks,
    sendMode,
    localSend: { date: localDate, time: localTime, fallbackTz },
    scheduledAt,
    now,
    timeZone,
  });
  const fallbackProblem =
    sendMode === 'local' && fallbackTz.trim() !== '' && !isValidTimeZone(fallbackTz.trim());
  const timeProblem =
    (sendMode === 'later' || sendMode === 'smart') && scheduledAt !== null
      ? checkSchedule(scheduledAt, now)
      : null;
  const sendLine =
    steps.time.state !== 'complete'
      ? null
      : sendMode === 'now'
        ? 'Sends right after approval.'
        : `Sends ${steps.time.summary}.`;
  const ready = readyCount(steps);
  const allMainReady = mainReady(steps);
  const unlocked = isReady(steps, checksPhase);
  const next = nextTarget(steps, checksPhase);

  const runChecks = useCallback(async () => {
    if (!id || checksBusy.current) return;
    checksBusy.current = true;
    revealTimers.current.forEach((t) => window.clearTimeout(t));
    revealTimers.current = [];
    setChecksError(null);
    setChecks((c) => ({ ...c, phase: 'running' }));
    setRevealed(0);
    try {
      await saveIfDirty();
      const at = checkSnap;
      const res = await apiFetch<PreflightResult>(`/campaigns/${id}/preflight`, {
        method: 'POST',
        body: {},
      });
      const done = () => {
        setChecks({ phase: res.ok ? 'pass' : 'fail', checks: res.checks });
        setCheckedSnap(at);
        setRevealed(res.checks.length);
        checksBusy.current = false;
      };
      setChecks({ phase: 'running', checks: res.checks });
      if (prefersReducedMotion() || res.checks.length === 0) {
        done();
        return;
      }
      res.checks.forEach((_, i) => {
        revealTimers.current.push(
          window.setTimeout(() => setRevealed(i + 1), REVEAL_FIRST_MS + i * REVEAL_STEP_MS),
        );
      });
      revealTimers.current.push(
        window.setTimeout(done, REVEAL_FIRST_MS + (res.checks.length - 1) * REVEAL_STEP_MS + 60),
      );
    } catch (err) {
      setChecks((c) => ({ ...c, phase: 'error' }));
      setChecksError(
        err instanceof ApiError && err.body.message
          ? err.body.message
          : 'The checks could not run. Try again.',
      );
      checksBusy.current = false;
    }
  }, [id, saveIfDirty, checkSnap]);

  useEffect(() => () => revealTimers.current.forEach((t) => window.clearTimeout(t)), []);

  // Opening Checks runs them when there is no current result.
  useEffect(() => {
    if (openStep !== 'checks' || readOnly || !allMainReady) return;
    if (checksPhase === 'idle' || checksPhase === 'stale') void runChecks();
  }, [openStep, checksPhase, readOnly, allMainReady, runChecks]);

  // "Sending unlocked" only on a real transition, not on first load.
  const wasUnlocked = useRef<boolean | null>(null);
  useEffect(() => {
    if (wasUnlocked.current === false && unlocked) {
      toast({
        title: 'Sending unlocked',
        description: 'All steps and checks are green.',
        icon: LockOpen,
      });
    }
    wasUnlocked.current = unlocked;
  }, [unlocked, toast]);

  const stepEnabled = useCallback(
    (sid: StepId) => !readOnly && steps[sid].state !== 'locked',
    [readOnly, steps],
  );

  const goStep = useCallback(
    (sid: StepId) => {
      if (!stepEnabled(sid)) {
        if (!readOnly) {
          toast({
            title: `${STEP_LABELS[sid]} is locked`,
            description: 'Every step and check must be green first.',
            icon: Lock,
            tone: 'muted',
          });
        }
        return;
      }
      setSheetOpen(false);
      setOpenStep(sid);
      focusStepRef.current = sid;
    },
    [stepEnabled, readOnly, toast],
  );

  useEffect(() => {
    const sid = focusStepRef.current;
    if (!sid) return;
    focusStepRef.current = null;
    const head = document.getElementById(`pf-h-${sid}`);
    head?.focus({ preventScroll: true });
    head?.scrollIntoView?.({
      block: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, [openStep]);

  const toggleStep = (sid: StepId) => setOpenStep((cur) => (cur === sid ? null : sid));

  const openBlock = (bid: string | null) => {
    setOpenBlockId(bid);
    if (bid) setFlash((f) => ({ id: bid, n: (f?.n ?? 0) + 1 }));
  };

  const fixCheck = (checkId: string) => {
    const target = stepForCheck(checkId);
    if (checkId === 'alt-text') {
      const img = blocks.find((b) => b.type === 'image' && !b.alt.trim());
      if (img) openBlock(img.id);
    }
    goStep(target);
  };

  const openSheet = () => {
    setDevice('mobile');
    setSheetOpen(true);
  };
  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    fabRef.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    if (sheetOpen && isMobile) closeRef.current?.focus({ preventScroll: true });
  }, [sheetOpen, isMobile]);
  useEffect(() => {
    if (!isMobile) setSheetOpen(false);
  }, [isMobile]);

  // --- keyboard: ⌘K, j/k, esc ---
  useEffect(() => {
    if (!campaign) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (paletteOpen || e.defaultPrevented) return;
      if (e.key === 'Escape') {
        if (sheetOpen) {
          e.preventDefault();
          closeSheet();
        } else if (openStep) {
          e.preventDefault();
          const sid = openStep;
          setOpenStep(null);
          document.getElementById(`pf-h-${sid}`)?.focus({ preventScroll: true });
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      const inRail = e.target instanceof Element && e.target.closest('.pf-rail') !== null;
      const down = e.key === 'j' || (inRail && e.key === 'ArrowDown');
      const up = e.key === 'k' || (inRail && e.key === 'ArrowUp');
      if (!down && !up) return;
      const heads = Array.from(
        document.querySelectorAll<HTMLButtonElement>('.pf-rail [data-step-head]:not(:disabled)'),
      );
      if (heads.length === 0) return;
      e.preventDefault();
      const cur = heads.findIndex((h) => h === document.activeElement);
      const idx =
        cur === -1
          ? down
            ? 0
            : heads.length - 1
          : (cur + (down ? 1 : -1) + heads.length) % heads.length;
      heads[idx]?.focus();
      heads[idx]?.scrollIntoView?.({ block: 'nearest' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [campaign, paletteOpen, sheetOpen, openStep, closeSheet]);

  const buildPaletteItems = (): PaletteItem[] => {
    const close = (fn: () => void) => () => {
      setPaletteOpen(false);
      fn();
    };
    const items: PaletteItem[] = ALL_STEPS.map((sid) => ({
      key: `step-${sid}`,
      group: 'Steps',
      title: STEP_LABELS[sid],
      sub: steps[sid].summary,
      icon: STEP_ICONS[sid],
      run: close(() => goStep(sid)),
    }));
    if (canSendTest) {
      items.push({
        key: 'test',
        group: 'Actions',
        title: 'Send test email',
        sub: testEmail || undefined,
        icon: <Send aria-hidden="true" />,
        run: close(sendTest),
      });
    }
    if (!readOnly && allMainReady) {
      items.push({
        key: 'checks',
        group: 'Actions',
        title: 'Run checks',
        icon: <RotateCw aria-hidden="true" />,
        run: close(() => {
          setOpenStep('checks');
          void runChecks();
        }),
      });
    }
    items.push(
      device === 'desktop' || isMobile
        ? {
            key: 'mobile',
            group: 'Actions',
            title: 'Preview on mobile width',
            icon: <Smartphone aria-hidden="true" />,
            run: close(() => (isMobile ? openSheet() : setDevice('mobile'))),
          }
        : {
            key: 'desktop',
            group: 'Actions',
            title: 'Preview on desktop width',
            icon: <Monitor aria-hidden="true" />,
            run: close(() => setDevice('desktop')),
          },
    );
    if (canSave) {
      items.push({
        key: 'save',
        group: 'Actions',
        title: 'Save draft',
        icon: <Save aria-hidden="true" />,
        run: close(saveDraft),
      });
    }
    return items;
  };

  if (isLoading) {
    return (
      <EditorFrame>
        <p className="pf-rail pf-loading" role="status">
          Loading campaign…
        </p>
      </EditorFrame>
    );
  }
  if (isError || !campaign) {
    return (
      <EditorFrame>
        <div className="pf-rail">
          <div className="pf-alert" role="alert">
            {isError ? campaignLoadErrorMessage(error) : 'The campaign was not found.'}
          </div>
          <Link to="/kampagner">Back to campaigns</Link>
        </div>
      </EditorFrame>
    );
  }

  const blockErrors = editorMode === 'blocks' ? validateBlocks(blocks) : [];
  const statusText = saveMutation.isPending
    ? 'Saving…'
    : readOnly
      ? `Read only, ${campaign.status}`
      : dirty
        ? 'Unsaved changes'
        : 'Draft, saved';
  const subjectOver = subject.length > SUBJECT_MAX;
  const subjectBOver = subjectB.length > SUBJECT_MAX;
  /** Content A as HTML, the starting point for content B (K6). */
  const contentAHtml = (): string => (editorMode === 'blocks' ? blocksToHtml(blocks) : htmlSource);
  const senderDomain = senderRow?.fromEmail.split('@')[1] ?? '';

  const stepProps = (sid: StepId) => ({
    id: sid,
    info: steps[sid],
    open: openStep === sid,
    disabled: !stepEnabled(sid),
    onToggle: () => toggleStep(sid),
  });

  return (
    <div className="pf">
      <SkipLink />
      <EditorNav />
      <main id="main-content" className="pf-editor">
        <header className="pf-head">
          <div className="pf-crumbs">
            <Link to="/kampagner">Campaigns</Link>
            <span className="pf-sep" aria-hidden="true">
              /
            </span>
            <span className="pf-status" data-s={dirty ? 'dirty' : 'saved'} role="status">
              <span className="pf-dot" aria-hidden="true" />
              <span>{statusText}</span>
            </span>
            <div className="pf-headtools">
              {!readOnly ? (
                <button
                  type="button"
                  className="pf-btn pf-btn-sm"
                  disabled={!canSave || saveMutation.isPending}
                  onClick={saveDraft}
                >
                  <Save aria-hidden="true" />
                  Save draft
                </button>
              ) : null}
              <button
                type="button"
                className="pf-jump"
                aria-label="Jump to a step"
                aria-keyshortcuts="Meta+K Control+K"
                aria-haspopup="dialog"
                onClick={() => setPaletteOpen(true)}
              >
                <Search aria-hidden="true" />
                <span className="pf-jl">Jump to</span>
                <kbd>⌘K</kbd>
              </button>
            </div>
          </div>
          <div className="pf-titlerow">
            <h1 className="pf-sr">{campaign.name}</h1>
            <input
              className="pf-title"
              aria-label="Name (internal)"
              value={name}
              disabled={readOnly}
              onChange={(e) => setName(e.target.value)}
            />
            {MOCK_MODE ? (
              <span className="pf-demo" title="All campaign data on this page is sample data">
                Sample data
              </span>
            ) : null}
          </div>
        </header>

        <div className="pf-rail">
          {readOnly ? (
            <div className="pf-sentbar" role="status">
              <div
                className="pf-sentbar-ico"
                data-tone={campaign.status === 'sent' ? 'ok' : 'muted'}
              >
                <Lock aria-hidden="true" />
              </div>
              <div>
                <b>{campaign.status === 'sent' ? 'Sent and locked' : 'Read only'}</b>
                <span className="pf-sentbar-txt">
                  {campaign.status !== 'draft'
                    ? `Only drafts can be edited. This campaign is "${campaign.status}".`
                    : 'Only editors can change this draft.'}
                  {campaign.status === 'sent' ? ' Sending cannot be recalled.' : ''}
                </span>
              </div>
              {campaign.status !== 'draft' ? (
                <div className="pf-sentbar-actions">
                  {isEditor ? (
                    <button
                      type="button"
                      className="pf-btn pf-btn-sm"
                      disabled={duplicating}
                      onClick={() => duplicate(campaign.id)}
                    >
                      <Copy aria-hidden="true" />
                      {duplicating ? 'Duplicating…' : 'Duplicate'}
                    </button>
                  ) : null}
                  <Link to={`/kampagner/${campaign.id}/send`} className="pf-btn pf-btn-sm">
                    Go to sending
                  </Link>
                </div>
              ) : null}
            </div>
          ) : (
            <FlightCard
              steps={steps}
              ready={ready}
              next={next}
              sendLine={sendLine}
              onNext={() => goStep(next.go)}
            />
          )}

          {saveError ? (
            <div className="pf-alert" role="alert">
              {saveError}
            </div>
          ) : null}
          {duplicateError ? (
            <div className="pf-alert" role="alert">
              {duplicateError}
            </div>
          ) : null}

          <ol className={`pf-list${readOnly ? ' pf-locked-all' : ''}`} aria-label="Campaign steps">
            <ChecklistStep {...stepProps('to')}>
              <AudiencePicker
                embedded
                value={audience}
                onChange={setAudience}
                disabled={readOnly}
              />
            </ChecklistStep>

            <ChecklistStep {...stepProps('from')}>
              <div className="pf-field">
                <SenderSelect
                  id="c-sender"
                  value={senderId}
                  disabled={readOnly}
                  onChange={setSenderId}
                />
              </div>
              {senderRow ? (
                senderRow.domainStatus === 'verified' ? (
                  <p className="pf-hint">
                    The domain {senderDomain} is verified. Manage senders in{' '}
                    <Link to="/indstillinger/afsendere">Settings</Link>.
                  </p>
                ) : (
                  <p className="pf-hint pf-hint-warn">
                    <TriangleAlert aria-hidden="true" />
                    <span>
                      The domain {senderDomain} is not verified yet. Verify it in{' '}
                      <Link to="/indstillinger/afsendere">Settings</Link>.
                    </span>
                  </p>
                )
              ) : null}
            </ChecklistStep>

            <ChecklistStep {...stepProps('subject')}>
              <div className="pf-field">
                <div className="pf-flabel">
                  <label htmlFor="c-subject">Subject line</label>
                  <span className="pf-count" data-over={subjectOver} aria-hidden="true">
                    {subject.length}/{SUBJECT_MAX}
                  </span>
                </div>
                <input
                  id="c-subject"
                  autoComplete="off"
                  value={subject}
                  disabled={readOnly}
                  data-warn={subjectOver || undefined}
                  aria-describedby={subjectOver ? 'c-subject-hint' : undefined}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setTypingKey((k) => k + 1);
                  }}
                />
                {subjectOver ? (
                  <p className="pf-hint" id="c-subject-hint">
                    Over {SUBJECT_MAX} characters, most inboxes cut it off.
                  </p>
                ) : null}
              </div>
              <div className="pf-field">
                <div className="pf-flabel">
                  <label htmlFor="c-preheader">Preview text</label>
                  <span
                    className="pf-count"
                    data-over={preheader.length >= PREHEADER_MAX}
                    aria-hidden="true"
                  >
                    {preheader.length}/{PREHEADER_MAX}
                  </span>
                </div>
                <input
                  id="c-preheader"
                  autoComplete="off"
                  value={preheader}
                  maxLength={PREHEADER_MAX}
                  disabled={readOnly}
                  placeholder="Optional, shown after the subject in the inbox"
                  aria-describedby="c-preheader-hint"
                  onChange={(e) => {
                    setPreheader(e.target.value);
                    setTypingKey((k) => k + 1);
                  }}
                />
                <p className="pf-hint" id="c-preheader-hint">
                  Inboxes show this after the subject line. Leave it empty and they show the first
                  line of the email instead.
                  {abEnabled ? ' Both subject lines share the same preview text.' : ''}
                </p>
              </div>
              <div className="pf-switchrow">
                <Switch
                  checked={abEnabled}
                  disabled={readOnly}
                  onChange={() => setAbEnabled((v) => !v)}
                  label="Turn A/B test on or off"
                />
                <span aria-hidden="true">A/B test a second subject line</span>
              </div>
              {abEnabled ? (
                <div className="pf-reveal">
                  <div>
                    <div className="pf-field">
                      <div className="pf-flabel">
                        <label htmlFor="c-subject-b">Subject line B</label>
                        <span className="pf-count" data-over={subjectBOver} aria-hidden="true">
                          {subjectB.length}/{SUBJECT_MAX}
                        </span>
                      </div>
                      <input
                        id="c-subject-b"
                        autoComplete="off"
                        value={subjectB}
                        disabled={readOnly}
                        data-warn={subjectBOver || undefined}
                        placeholder={
                          contentBEnabled
                            ? 'Optional: leave empty to test only the content'
                            : 'Alternative subject line for the test round'
                        }
                        onChange={(e) => {
                          setSubjectB(e.target.value);
                          setTypingKey((k) => k + 1);
                        }}
                      />
                    </div>
                    <div className="pf-switchrow">
                      <Switch
                        checked={contentBEnabled}
                        disabled={readOnly}
                        onChange={() => {
                          // Content B starts as a copy of A, ready to change.
                          if (!contentBEnabled && !htmlB.trim()) setHtmlB(contentAHtml());
                          setContentBEnabled((v) => !v);
                          setTypingKey((k) => k + 1);
                        }}
                        label="Test content too"
                      />
                      <span aria-hidden="true">Test content too</span>
                    </div>
                    {contentBEnabled ? (
                      <div className="pf-field">
                        <div className="pf-flabel">
                          <span>Content B (HTML)</span>
                          {!readOnly ? (
                            <button
                              type="button"
                              className="pf-btn pf-btn-sm"
                              onClick={() => {
                                setHtmlB(contentAHtml());
                                setTypingKey((k) => k + 1);
                              }}
                            >
                              Copy content A again
                            </button>
                          ) : null}
                        </div>
                        <p className="pf-hint" id="c-html-b-hint">
                          Group B gets this instead of the content above. It starts as a copy of
                          content A in HTML, so change only what you want to test (a headline, an
                          image, a button). Use the A/B switch in the preview to see it.
                        </p>
                        <HtmlSourceEditor
                          id="c-html-b"
                          label="HTML source B"
                          value={htmlB}
                          onChange={(next) => {
                            setHtmlB(next);
                            setTypingKey((k) => k + 1);
                          }}
                          disabled={readOnly}
                          className="h-[280px]"
                        />
                      </div>
                    ) : null}
                    <div className="pf-row2">
                      <div className="pf-field">
                        <label className="pf-flabel" htmlFor="c-ab-split">
                          Test share per variant (%)
                        </label>
                        <input
                          id="c-ab-split"
                          type="number"
                          min={5}
                          max={50}
                          value={abSplitPercent}
                          disabled={readOnly}
                          onChange={(e) => setAbSplitPercent(Number(e.target.value))}
                        />
                      </div>
                      <div className="pf-field">
                        <label className="pf-flabel" htmlFor="c-ab-wait">
                          Wait time before winner (hours)
                        </label>
                        <input
                          id="c-ab-wait"
                          type="number"
                          min={1}
                          max={24}
                          value={abWaitHours}
                          disabled={readOnly}
                          onChange={(e) => setAbWaitHours(Number(e.target.value))}
                        />
                      </div>
                    </div>
                    <div className="pf-row2">
                      <div className="pf-field">
                        <label className="pf-flabel" htmlFor="c-ab-metric">
                          Winner is decided by
                        </label>
                        <select
                          id="c-ab-metric"
                          value={abMetric}
                          disabled={readOnly}
                          onChange={(e) =>
                            setAbMetric(e.target.value === 'opens' ? 'opens' : 'clicks')
                          }
                        >
                          <option value="clicks">Unique clicks</option>
                          <option value="opens">Unique opens</option>
                        </select>
                      </div>
                    </div>
                    <div className="pf-switchrow">
                      <Switch
                        checked={abAutoSend}
                        disabled={readOnly}
                        onChange={() => setAbAutoSend((v) => !v)}
                        label="Send the winner to the rest automatically"
                      />
                      <span aria-hidden="true">Send the winner to the rest automatically</span>
                    </div>
                    <p className="pf-hint">
                      {contentBEnabled
                        ? `${abSplitPercent}% of recipients get version A, ${abSplitPercent}% get version B${subjectB.trim() ? ' (subject line B and content B)' : ' (content B, same subject line)'}. `
                        : `${abSplitPercent}% of recipients get subject line A, ${abSplitPercent}% get subject line B. `}
                      {abAutoSend
                        ? `After ${abWaitHours} hours the rest are sent the winner (most unique ${abMetric}). Too few to decide a winner: the rest get ${contentBEnabled ? 'version' : 'subject line'} A.`
                        : `The rest wait until you pick the winner on the campaign's statistics page (after ${abWaitHours} hours you see the result there).`}
                    </p>
                  </div>
                </div>
              ) : null}
            </ChecklistStep>

            <ChecklistStep {...stepProps('content')}>
              {editorMode === 'html' ? (
                <div className="pf-field">
                  <p className="pf-note">
                    This campaign is HTML, not blocks.{' '}
                    {readOnly
                      ? 'The HTML is shown as it was sent.'
                      : 'Edit the HTML below; the preview updates as you type. It is checked and cleaned by the server when you save.'}
                  </p>
                  <HtmlSourceEditor
                    id="c-html"
                    value={htmlSource}
                    onChange={setHtmlSource}
                    disabled={readOnly || copyingImages}
                    className="h-[360px]"
                  />
                  {htmlCheck.report && htmlCheck.report.problems.length > 0 ? (
                    <div className="pf-note" role="note">
                      <ul className="m-0 pl-4">
                        {htmlCheck.report.problems.map((pr, i) => (
                          <li key={`${pr.code}-${pr.tag ?? ''}-${i}`}>
                            {pr.tag ? `${pr.tag} ` : ''}
                            {pr.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {!readOnly && htmlCheck.report && htmlCheck.report.images.total > 0 ? (
                    <div className="flex flex-col gap-1.5 text-sm">
                      <CopyImagesPanel
                        images={htmlCheck.report.images}
                        run={rehost.run}
                        onCopy={() => void rehost.start()}
                        disabled={htmlCheck.pending}
                      />
                    </div>
                  ) : null}
                  {campaign?.sections && htmlSource !== campaign.html ? (
                    <SectionEditsNotice />
                  ) : null}
                  <div className="pf-sendrow">
                    {dirty ? (
                      <button type="button" className="pf-btn pf-btn-sm" disabled>
                        Edit sections
                      </button>
                    ) : (
                      <Link to={`/kampagner/${id}/sections`} className="pf-btn pf-btn-sm">
                        Edit sections
                      </Link>
                    )}
                    <span className="pf-hint">
                      {dirty
                        ? 'Save your changes first.'
                        : 'Change texts, images and links, and move sections, without touching the HTML.'}
                    </span>
                  </div>
                  {!readOnly ? (
                    <div className="pf-sendrow">
                      <button
                        type="button"
                        className="pf-btn pf-btn-sm"
                        onClick={() => {
                          setBlocks(seedBlocks(footerDefaults));
                          setEditorMode('blocks');
                        }}
                      >
                        Replace with blocks
                      </button>
                      <span className="pf-hint">
                        Throws away this HTML and starts a block layout. Takes effect when you save.
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : editorMode === 'blocks' ? (
                <>
                  {blockErrors.length > 0 ? (
                    <div className="pf-note" role="note">
                      <ul className="m-0 pl-4">
                        {blockErrors.map((err) => (
                          <li key={err}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <BlockEditor
                    variant="rows"
                    blocks={blocks}
                    onChange={setBlocks}
                    openBlockId={openBlockId}
                    onOpenBlockChange={openBlock}
                    onHighlight={setHighlightId}
                    disabled={readOnly}
                    footerDefaults={footerDefaults}
                  />
                </>
              ) : (
                <p className="pf-hint">Loading content…</p>
              )}
              <div className="pf-switchrow mt-3">
                <Switch
                  checked={couponEnabled}
                  disabled={readOnly}
                  onChange={() => setCouponEnabled((v) => !v)}
                  label="Turn personal discount code on or off"
                />
                <span aria-hidden="true">Personal discount code</span>
              </div>
              {couponEnabled ? (
                <div className="pf-reveal">
                  <div>
                    <div className="pf-row2">
                      <div className="pf-field">
                        <label className="pf-flabel" htmlFor="c-coupon-percent">
                          Discount (%)
                        </label>
                        <input
                          id="c-coupon-percent"
                          type="number"
                          min={COUPON_PERCENT_MIN}
                          max={COUPON_PERCENT_MAX}
                          value={couponPercent}
                          disabled={readOnly}
                          onChange={(e) => setCouponPercent(Number(e.target.value))}
                        />
                      </div>
                      <div className="pf-field">
                        <label className="pf-flabel" htmlFor="c-coupon-days">
                          Valid for (days)
                        </label>
                        <input
                          id="c-coupon-days"
                          type="number"
                          min={COUPON_VALID_DAYS_MIN}
                          max={COUPON_VALID_DAYS_MAX}
                          value={couponValidDays}
                          disabled={readOnly}
                          onChange={(e) => setCouponValidDays(Number(e.target.value))}
                        />
                      </div>
                    </div>
                    <div className="pf-field">
                      <label className="pf-flabel" htmlFor="c-coupon-prefix">
                        Code prefix
                      </label>
                      <input
                        id="c-coupon-prefix"
                        autoComplete="off"
                        value={couponPrefix}
                        maxLength={COUPON_PREFIX_MAX}
                        disabled={readOnly}
                        data-warn={couponProblem !== null || undefined}
                        aria-describedby="c-coupon-hint"
                        onChange={(e) =>
                          setCouponPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                        }
                      />
                    </div>
                    <p className="pf-hint" id="c-coupon-hint">
                      {couponProblem ??
                        `The email must contain {{ coupon_code }}. Each recipient gets their own single-use code, locked to their email address (like ${couponPrefix}-7KQ4M2XC).`}
                    </p>
                  </div>
                </div>
              ) : null}
              {isEditor && editorMode !== 'loading' ? (
                <div className="pf-sendrow mt-3">
                  <SaveAsTemplateSheet
                    defaultName={name ? `${name} (template)` : ''}
                    content={
                      editorMode === 'html'
                        ? { html: htmlSource }
                        : { html: blocksToHtml(blocks), blockJson: { blocks } }
                    }
                  />
                  <PreviewAsContact
                    html={editorMode === 'html' ? htmlSource : blocksToHtml(blocks)}
                    subject={subject}
                  />
                </div>
              ) : null}
            </ChecklistStep>

            <ChecklistStep {...stepProps('time')}>
              <div className="pf-field">
                <span className="pf-flabel">When</span>
                <SegmentedControl
                  label="When to send"
                  value={sendMode}
                  onChange={(v) => {
                    if (readOnly) return;
                    setSendMode(v);
                  }}
                  options={[
                    {
                      value: 'now',
                      label: (
                        <>
                          <Zap aria-hidden="true" />
                          Right after approval
                        </>
                      ),
                      ariaLabel: 'Right after approval',
                      disabled: readOnly,
                    },
                    {
                      value: 'later',
                      label: (
                        <>
                          <CalendarClock aria-hidden="true" />
                          Schedule
                        </>
                      ),
                      ariaLabel: 'Schedule',
                      disabled: readOnly,
                    },
                    {
                      value: 'local',
                      label: (
                        <>
                          <Globe aria-hidden="true" />
                          Local time
                        </>
                      ),
                      ariaLabel: "Local time per recipient's country",
                      disabled: readOnly,
                    },
                    {
                      value: 'smart',
                      label: (
                        <>
                          <Sparkles aria-hidden="true" />
                          Smart
                        </>
                      ),
                      ariaLabel: "Smart send time: each contact's usual hour",
                      disabled: readOnly,
                    },
                  ]}
                />
                {sendMode === 'local' ? (
                  <>
                    <div className="pf-when">
                      <div className="pf-when-cell">
                        <label className="pf-flabel" htmlFor="c-local-date">
                          Date
                        </label>
                        <input
                          id="c-local-date"
                          type="date"
                          value={localDate}
                          disabled={readOnly}
                          aria-describedby="c-local-hint"
                          onChange={(e) => setLocalDate(e.target.value)}
                        />
                      </div>
                      <div className="pf-when-cell">
                        <label className="pf-flabel" htmlFor="c-local-time">
                          Local time
                        </label>
                        <input
                          id="c-local-time"
                          type="time"
                          value={localTime}
                          disabled={readOnly}
                          aria-describedby="c-local-hint"
                          onChange={(e) => setLocalTime(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="pf-when">
                      <div className="pf-when-cell">
                        <label className="pf-flabel" htmlFor="c-local-fallback">
                          Fallback time zone (optional)
                        </label>
                        <input
                          id="c-local-fallback"
                          type="text"
                          value={fallbackTz}
                          placeholder="Europe/Berlin"
                          disabled={readOnly}
                          aria-invalid={fallbackProblem || undefined}
                          aria-describedby="c-local-hint"
                          onChange={(e) => setFallbackTz(e.target.value)}
                        />
                      </div>
                    </div>
                    {fallbackProblem ? (
                      <p className="pf-error" role="alert">
                        Unknown time zone. Use a name like Europe/Berlin or Europe/Copenhagen.
                      </p>
                    ) : null}
                    <p className="pf-hint" id="c-local-hint">
                      Each recipient gets the campaign at this time of day in their own time zone,
                      worked out from their country. Contacts with no known country, or a country we
                      have no time zone for, get the fallback time zone instead (Europe/Berlin when
                      you leave it empty). A country with several zones uses one of them. If the
                      time has already passed for a recipient, they get it right away once the
                      campaign is approved. The campaign still has to be approved on the next
                      screen.
                    </p>
                  </>
                ) : sendMode === 'later' || sendMode === 'smart' ? (
                  <>
                    <div className="pf-when">
                      <div className="pf-when-cell">
                        <label className="pf-flabel" htmlFor="c-send-date">
                          Date
                        </label>
                        <input
                          id="c-send-date"
                          type="date"
                          value={schedDate}
                          disabled={readOnly}
                          aria-invalid={timeProblem !== null || undefined}
                          aria-describedby="c-send-hint"
                          onChange={(e) => setSchedDate(e.target.value)}
                        />
                      </div>
                      <div className="pf-when-cell">
                        <label className="pf-flabel" htmlFor="c-send-time">
                          Time
                        </label>
                        <input
                          id="c-send-time"
                          type="time"
                          value={schedTime}
                          disabled={readOnly}
                          aria-invalid={timeProblem !== null || undefined}
                          aria-describedby="c-send-hint"
                          onChange={(e) => setSchedTime(e.target.value)}
                        />
                      </div>
                    </div>
                    {timeProblem ? (
                      <p className="pf-error" role="alert">
                        {timeProblem === 'past'
                          ? 'This time has already passed. Pick a time in the future.'
                          : 'The send time can be at most one year ahead.'}
                      </p>
                    ) : null}
                    <p className="pf-hint" id="c-send-hint">
                      Times are in your browser time zone ({timeZone}) and stored as UTC.
                      {scheduledAt !== null && timeProblem === null
                        ? ` Sends ${formatSendTime(scheduledAt, timeZone)}.`
                        : ''}{' '}
                      The campaign still has to be approved on the next screen before this time.
                    </p>
                    {sendMode === 'smart' ? (
                      <div className="pf-hint" id="c-smart-hint" aria-live="polite">
                        <p>
                          This is when the 24-hour sending window starts. Everyone gets the campaign
                          inside that window: each contact at the hour of the day they usually open
                          or click, worked out from their past human opens and clicks (bots left
                          out). A contact needs at least {smartCoverage.data?.minEvents ?? 3} opens
                          or clicks, at least half of them within the same three hours; otherwise
                          they get the campaign at the start time above.
                        </p>
                        {audience.listIds.length === 0 ? (
                          <p>Pick an audience to see how many contacts have enough data.</p>
                        ) : smartCoverage.isError ? (
                          <p role="alert">Could not count contacts with enough data.</p>
                        ) : smartCoverage.data ? (
                          <p>
                            <strong>{smartCoverage.data.withData.toLocaleString('en-US')}</strong>{' '}
                            of {smartCoverage.data.recipients.toLocaleString('en-US')} recipients
                            have enough data and get it at their usual hour.{' '}
                            <strong>{smartCoverage.data.fallback.toLocaleString('en-US')}</strong>{' '}
                            get it at the start time. The counts are updated once a night, so very
                            recent opens are not in yet.
                          </p>
                        ) : (
                          <p>Counting contacts with enough data…</p>
                        )}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="pf-hint">
                    The campaign goes out as soon as it is approved on the next screen. Choose
                    Schedule to send at a set time instead.
                  </p>
                )}
              </div>
            </ChecklistStep>
          </ol>

          <div className="pf-group" aria-hidden="true">
            Before sending
          </div>
          <ol className={`pf-list${readOnly ? ' pf-locked-all' : ''}`} aria-label="Before sending">
            <ChecklistStep {...stepProps('checks')}>
              {checks.checks.length > 0 ? (
                <ul className="pf-checks" aria-label="Check results">
                  {checks.checks.map((c, i) => {
                    const shown = checks.phase !== 'running' || i < revealed;
                    const state: StepState = shown ? CHECK_STATE[c.status] : 'running';
                    return (
                      <li key={c.id} className="pf-ck-row" data-s={state}>
                        <StepGlyph state={state} />
                        <span>{c.title}</span>
                        <span className="pf-cr">
                          {shown ? c.message : 'Checking…'}
                          {shown && c.status === 'fail' ? (
                            <button type="button" className="pf-fix" onClick={() => fixCheck(c.id)}>
                              Fix
                              <span className="pf-sr"> {c.title}</span>
                            </button>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : checks.phase === 'running' ? (
                <p className="pf-hint" role="status">
                  Running the checks…
                </p>
              ) : (
                <p className="pf-hint">
                  Links, unsubscribe link, alt text, email size and the subject line are checked on
                  the saved draft.
                </p>
              )}
              {checksError ? (
                <p className="pf-error" role="alert">
                  {checksError}
                </p>
              ) : null}
              <div className="pf-sendrow">
                <button
                  type="button"
                  className="pf-btn pf-btn-sm"
                  disabled={readOnly || checksPhase === 'running'}
                  onClick={() => void runChecks()}
                >
                  <RotateCw aria-hidden="true" />
                  Run checks
                </button>
                <span className="pf-hint" role="status">
                  {checksPhase === 'stale'
                    ? 'Content changed since the last run.'
                    : checksPhase === 'pass' || checksPhase === 'fail'
                      ? 'Checked just now.'
                      : ''}
                </span>
              </div>
            </ChecklistStep>

            <ChecklistStep {...stepProps('send')}>
              {unlocked ? (
                <div className="pf-sendbox">
                  <div className="pf-field">
                    <span className="pf-flabel">Send a test first</span>
                    <div className="pf-testrow">
                      <input
                        id="c-test-to"
                        aria-label="Test email recipient"
                        type="email"
                        placeholder="you@mail.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                      />
                      <button
                        type="button"
                        className="pf-btn"
                        disabled={!testEmail || testSendMutation.isPending}
                        onClick={sendTest}
                      >
                        <Send aria-hidden="true" />
                        Send test email
                      </button>
                    </div>
                    <p className="pf-hint">
                      Only users with access to the account can receive test emails.
                      {campaign.testSendDoneAt
                        ? ` Last test sent ${formatDateTime(campaign.testSendDoneAt)}.`
                        : ''}
                    </p>
                    {testSendMsg ? (
                      <p className="pf-hint" role="status">
                        {testSendMsg}
                      </p>
                    ) : null}
                  </div>
                  <ol className="pf-nextsteps">
                    <li>
                      <b>Approval.</b> The next screen runs the checks once more and shows the exact
                      recipient count.
                    </li>
                    <li>
                      <b>Confirm.</b> Type that count and the code from your authenticator app to
                      send.
                    </li>
                  </ol>
                  <div className="pf-irrev">
                    <OctagonAlert aria-hidden="true" />
                    <span>Sending cannot be recalled. Once sent, this campaign is locked.</span>
                  </div>
                  <button
                    type="button"
                    className="pf-btn pf-btn-pri pf-btn-send"
                    disabled={saveMutation.isPending}
                    onClick={() => {
                      void saveMutation
                        .mutateAsync()
                        .then(() => navigate(`/kampagner/${campaign.id}/send`))
                        .catch(() => {});
                    }}
                  >
                    <SendHorizontal aria-hidden="true" />
                    Continue to approval
                  </button>
                </div>
              ) : (
                <p className="pf-locknote">
                  <Lock aria-hidden="true" />
                  Unlocks when every step above is green.
                </p>
              )}
            </ChecklistStep>
          </ol>

          <div className="pf-railfoot" aria-hidden="true">
            <span>
              <kbd>j</kbd>
              <kbd>k</kbd> move
            </span>
            <span>
              <kbd>↵</kbd> open
            </span>
            <span>
              <kbd>esc</kbd> close
            </span>
            <span>
              <kbd>⌘K</kbd> jump
            </span>
          </div>
        </div>
      </main>

      <EmailPreview
        blocks={blocks}
        editorMode={editorMode}
        html={htmlCheck.report?.previewHtml ?? htmlSource}
        subject={subject}
        subjectB={subjectB}
        htmlB={contentBEnabled ? htmlB : ''}
        preheader={preheader}
        abEnabled={abEnabled}
        fromName={senderRow?.fromName ?? null}
        device={device}
        onDeviceChange={setDevice}
        canSendTest={canSendTest}
        testPending={testSendMutation.isPending}
        onSendTest={sendTest}
        highlightId={highlightId}
        flash={flash}
        typingKey={typingKey}
        mockMode={MOCK_MODE}
        isMobile={isMobile}
        sheetOpen={sheetOpen}
        onCloseSheet={closeSheet}
        closeRef={closeRef}
      />

      <button
        ref={fabRef}
        type="button"
        className="pf-btn pf-fab"
        aria-haspopup="dialog"
        aria-expanded={sheetOpen}
        onClick={openSheet}
      >
        <Eye aria-hidden="true" />
        Preview
        <span className="pf-fab-sub">
          {readOnly ? campaign.status : `${ready} of ${MAIN_STEPS.length} ready`}
        </span>
      </button>

      {paletteOpen ? (
        <CommandPalette items={buildPaletteItems()} onClose={() => setPaletteOpen(false)} />
      ) : null}
      <ToastStack toasts={toasts} onLeave={leaveToast} />
    </div>
  );
}
